import type { KioskTestimonial } from './hooks';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MAX_DISPLAY = 6;

function Stars({ rating, color }: { rating: number; color: string }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i <= rating ? color : '#E5E5EA'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

interface Props {
  testimonials: KioskTestimonial[];
  accentColor: string;
  headingFont: string;
}

export function KioskTestimonials({ testimonials, accentColor, headingFont }: Props) {
  if (!testimonials.length) return null;

  const display = testimonials.slice(0, MAX_DISPLAY);
  const avg = testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length;
  const avgRounded = Math.round(avg * 10) / 10;

  return (
    <>
      <style>{`
        .testimonial-scroll {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 640px) {
          .testimonial-scroll {
            display: flex !important;
            overflow-x: auto !important;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            gap: 12px;
            padding-bottom: 8px;
            scrollbar-width: none;
          }
          .testimonial-scroll::-webkit-scrollbar { display: none; }
          .testimonial-card { scroll-snap-align: start; min-width: 260px; max-width: 280px; flex-shrink: 0; }
        }
      `}</style>
      <section style={{ padding: '32px 0', borderBottom: '1px solid #F1F3F5' }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 6px' }}>
          What clients say
        </h2>

        {/* Summary bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <Stars rating={Math.round(avg)} color={accentColor} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', fontFamily: BODY_FONT }}>{avgRounded}</span>
          <span style={{ fontSize: 13, color: '#8E8E93', fontFamily: BODY_FONT }}>({testimonials.length} {testimonials.length === 1 ? 'review' : 'reviews'})</span>
        </div>

        {/* Cards */}
        <div className="testimonial-scroll">
          {display.map(t => (
            <div key={t.id} className="testimonial-card" style={{
              background: '#fff', borderRadius: 12, padding: 18,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F1F3F5',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <Stars rating={t.rating} color={accentColor} />
              <p style={{
                fontSize: 14, color: '#1A1A1A', lineHeight: 1.5, fontFamily: BODY_FONT,
                margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {t.text}
              </p>
              <div style={{ fontSize: 12, color: '#8E8E93', fontFamily: BODY_FONT, marginTop: 'auto' }}>
                — {t.name}{t.service ? ` · ${t.service}` : ''}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
